import { Translations } from "../types";

/**
 * English Translations
 */
export const en: Translations = {
  common: {
    continue: "Continue",
    retry: "Retry",
    ignore: "Ignore",
    loading: "Loading...",
    select: "Select",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    confirm: "Confirm",
    back: "Back",
    close: "Close",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    new: "New",
    yes: "Yes",
    no: "No",
    or: "or",
    and: "and",
    show: "Show",
    hide: "Hide",
    advanced: "Advanced",
    enabled: "Enabled",
    disabled: "Disabled",
  },

  menu: {
    profile: "Profile",
    play: "PLAY",
    playAgain: "Play Again",
    shop: "Shop",
    options: "Options",
    credits: "Credits",
    referral: "Invite",
    labs: "Labs",
    dailyRewards: "Rewards",
    battlePass: "Battle Pass",
    creators: "Creators",
    marketplace: "Marketplace",
    social: "Social",
    clan: "Clan",
    replays: "Replays",
    chooseMode: "🎮 Choose Mode",
    selectHowToPlay: "Select how you want to play",
    playSolo: "Play Solo",
    playSoloDesc: "Endless battle against AI",
    storyMode: "Story Mode",
    storyModeDesc: "Discover Arthur Ren's story",
    multiplayer: "Multiplayer",
    multiplayerDesc: "Create or join an online room",
    tournaments: "Tournaments",
    tournamentsDesc: "Competitions with prizes",
    ranking: "Ranking",
    rankingDesc: "See your global position",
    experimental: "Labs",
    experimentalDesc: "Experimental features",
    competitive: "Competitive",
    competitiveDesc: "Ranking, tournaments and leaderboard",
    infiniteBattle: "Endless Battle",
    infiniteBattleDesc: "Endless waves. Survive as long as you can!",
    battleRoyaleDesc: "Up to 300 tanks. Last one standing wins!",
    genAIMode: "GenAI Mode",
    genAIModeDesc: "Play against or watch generative AIs",
    tagRoguelike: "Roguelike",
    tagTerrains: "6 Terrains",
    tagElectricBarrier: "Electric Barrier",
    tagUniqueItems: "6 Unique Items",
    tagHot: "Hot",
    tagNew: "New",
    tagSkins: "SKINS",
  },

  profile: {
    personalData: "Personal Data",
    birthDate: "Date of Birth (optional)",
    showFullIdHint: "Click to show/hide the full ID",
    copyId: "Copy ID",
    copy: "Copy",
    crossSaveTitle: "Cross-Save:",
    crossSaveDesc: "Link your Google or Discord account and your progress syncs automatically across devices (web, mobile, desktop).",
    uniqueIdTitle: "Unique Identifier",
    odysIdLabel: "Odys ID (for support)",
    odysIdHint: "This ID is unique and permanent. Use it when contacting support.",
    session: "Session",
    switchAccount: "Switch Account / Sign Out",
    switchAccountHint: "You will be signed out and returned to the login screen.",
    avatarGallery: "Avatar Gallery",
    avatarGalleryDesc: "Unlock new avatars by completing story mode chapters",
    avatarsUnlocked: "unlocked",
  },

  matchSetup: {
    configureMatch: "Match Setup",
    selectOpponents: "Choose the number of opponents",
    tanksInArena: "tanks in the arena",
    arenaSize: "Arena Size",
    arenaSizeDesc: "Shrinking electric barrier",
    units: "units",
    blocks: "Blocks",
    blocksDesc: "Density of destructible obstacles",
    blocksEstimate: "~{count} blocks",
    perfLimited: "(limited by performance)",
    densityNone: "None",
    densityFew: "Few",
    densityMany: "Many",
    environment: "Environment",
    environmentDesc: "Choose the battle setting",
    availableItems: "Available Items",
    powerUpsCount: "({count} power-ups)",
    itemsHint: "During the battle, power-ups spawn randomly across the arena. Collect them for temporary advantages!",
  },

  shop: {
    title: "✨ Shop",
    subtitle: "Customize your tank!",
    tabs: {
      mystery: "Boxes",
      skins: "Skins",
      effects: "Effects",
      crystals: "Crystals",
      tanks: "Tanks",
    },
    rarity: {
      all: "All",
      common: "Common",
      rare: "Rare",
      epic: "Epic",
      legendary: "Legendary",
    },
    // Mystery Boxes
    mysteryBoxes: "Mystery Boxes",
    mysteryBoxesDesc: "Open boxes and win random tanks!",
    mysteryBox: {
      common: "Common Box",
      rare: "Rare Box",
      epic: "Epic Box",
      legendary: "Legendary Box",
    },
    mysteryBoxTip: "💡 Tip: The higher the box rarity, the better chance of epic and legendary tanks!",
    // Crystals
    crystalPackages: "Crystal Packages",
    crystalPremiumDesc: "Crystals are used to buy premium items",
    bonusAmount: "+{bonus} bonus!",
    redirectingPayment: "Redirecting to payment...",
    securePaymentInfo: "Payments processed securely via Stripe. Crystals are added instantly.",
    // Tanks
    tanks: "Tanks",
    tanksUnlocked: "{unlocked}/{total} unlocked",
    tanksFound: "{count} tanks found",
    free: "Free",
    // Filters
    filters: "Filters",
    filterRarity: "Rarity",
    filterSize: "Size",
    filterShots: "Shots",
    sizeAll: "All",
    sizeMini: "Mini",
    sizeNormal: "Normal",
    sizeLarge: "Large",
    sizeTitan: "Titan",
    shotSingle: "Single",
    shotDouble: "Double",
    shotTriple: "Triple",
    // Skins
    skins: "Skins",
    skinsDesc: "Customize your tank's appearance",
    equip: "Equip",
    equippedLabel: "Equipped",
    // Effects
    effects: "Effects",
    effectsDesc: "Add unique visual effects",
    effectTypes: {
      trail: "Trail",
      explosion: "Explosion",
      shot: "Shot",
      aura: "Aura",
      movement: "Movement",
      special: "Special",
    },
    // Purchase modal
    tank: "Tank",
    skin: "Skin",
    effect: "Effect",
    cancel: "Cancel",
    tankSkins: "Tank Skins",
    tankSkinsDesc: "Change appearance in Solo and Multiplayer",
    skinsCount: "{count} skins",
    visualEffects: "Visual Effects",
    visualEffectsDesc: "Trails, explosions and auras!",
    effectsCount: "{count} effects",
    crystalDescription: "Unlock exclusive items!",
    popular: "POPULAR",
    bonus: "+{percent}%",
    crystals: "Crystals",
    securePayment: "Secure payment via Stripe",
    paymentNotConfigured: "Payment not configured",
    confirmPurchase: "Confirm Purchase",
    price: "Price",
    yourBalance: "Your balance",
    insufficientBalance: "⚠️ Insufficient balance",
    buy: "Buy",
    noBalance: "No Balance",
    use: "USE",
    active: "✓ ACTIVE",
    equipped: "equipped!",
    removed: "removed",
    unlocked: "🎉 \"{name}\" unlocked!",
    chipsInsufficient: "🪙 Not enough Chips!",
    crystalsInsufficient: "💎 Not enough Crystals!",
    chipsInfo: "💡 Earn Crystals with subscriptions! Chips: 10 base + 5/kill + 20/win",
    paymentCancelled: "Payment cancelled",
    crystalsAdded: "🎉 +{amount} Crystals added!",
    paymentUnavailable: "Payment system unavailable",
    openingCheckout: "Opening checkout...",
    effectRemoved: "Effect removed",
    generateNewCode: "Generate New Code",
    converted: "Converted!",
  },

  ads: {
    watchForBonus: "Watch ad → Bonus chips",
  },
  labs: {
    hardcoreMode: "Hardcore Mode",
    title: "🔬 Labs",
    subtitle: "Experimental features and updates",
    arMode: "Augmented Reality",
    arModeDesc: "Play Rocketanks on your table using the camera!",
    battleRoyale: "Battle Royale",
    battleRoyaleDesc: "50 tanks, 1 winner. Electric arena that shrinks!",
    vrMode: "VR Mode",
    vrModeDesc: "Coming soon...",
    advancedAI: "Advanced AI",
    advancedAIDesc: "Coming soon...",
    openWorld: "Open World",
    openWorldDesc: "Coming soon...",
    comingSoon: "Coming soon...",
    experimentalWarning: "💡 Labs features are experimental and may change or be removed. Your feedback is very important!",
    beta: "BETA",
  },

  ar: {
    checking: "Checking AR support...",
    unavailable: "AR not available",
    unavailableReason: "Your device does not support Augmented Reality",
    tip: "💡 Tip:",
    tipDevices: [
      "Android with Chrome",
      "iOS with Safari (WebAR SDK or camera)",
      "HTTPS connection",
    ],
    startAR: "🚀 Start AR",
    startCamera: "Start Camera",
    loading: "Loading...",
    exit: "Exit",
    instructions: "👆 Tap screen to move • 🔄 Drag to rotate camera",
    permission: "AR Mode",
    permissionTitle: "AR Mode",
    permissionDesc: "Point the camera at a flat surface and watch the tanks fight in augmented reality!",
    cameraAccess: "Rear camera access",
    touchToMove: "Touch to move and shoot",
    rotateCamera: "Rotate camera to look around",
    // New WebXR translations
    title: "AR Mode",
    notSupported: "AR not available",
    howItWorks: "How it works:",
    step1: "Point camera at a flat surface (table, floor)",
    step2: "A reticle will appear - tap to place the board",
    step3: "Use joystick and fire button to play!",
    placeBoard: "Place the Board",
    placeInstructions: "Move your phone until you see the reticle on a flat surface, then tap to place",
    webARStep1: "Launch advanced WebAR experience (8th Wall/Zappar)",
    webARStep2: "Point to the real environment and follow on-screen instructions",
    // Camera fallback mode
    cameraStep1: "Allow camera access on your device",
    cameraStep2: "The board will appear over the camera",
    modeWebXR: "AR mode with surface detection",
    modeWebARSDK: "Advanced WebAR mode (cross-platform)",
    modeCamera: "Simulated camera mode",
    tryCamera: "Try camera mode",
    openExternal: "Open externally",
    // Supported device list
    deviceAndroid: "Android with Chrome",
    deviceIOS: "iPhone/iPad with Safari",
    deviceQuest: "Oculus Quest Browser",
    // RocketAR Engine — Mode Selection
    selectEngine: "Choose Your AR Engine",
    selectEngineDesc: "Select the best mode for your device",
    recommendedBadge: "Recommended",
    unavailableBadge: "Not Available",
    selectMode: "Select",
    backToSelection: "Back to AR selection",
    modeARCore: "ARCore Native",
    descWebXR: "Full AR with surface detection, hit-test and real-world anchoring",
    descARCore: "Native Android AR with best performance and precise tracking",
    descWebARSDK: "Cross-platform WebAR using 8th Wall/Zappar, works on iOS and Android",
    descCamera: "Simple camera with 3D game board overlay, works on any device",
    reasonNoNative: "Only available in the native Android app",
    reasonNoWebXR: "Your browser does not support WebXR AR",
    reasonNoSDK: "WebAR SDK not configured for this deployment",
    reasonNoCamera: "No camera detected on this device",
  },

  game: {
    wave: "Wave",
    lives: "Lives",
    score: "Score",
    kills: "Kills",
    pause: "Pause",
    resume: "Resume",
    quit: "Quit",
    victory: "VICTORY!",
    defeat: "DEFEAT",
    nextWave: "Next Wave",
    waveComplete: "Wave Complete!",
    enemiesDefeated: "Enemies Defeated",
    bonus: "Bonus",
    total: "Total",
    gameOver: "GAME OVER",
    finalScore: "Final Score",
    play: "PLAY",
    playAgain: "Play Again",
    playSubtitle: "Solo • Story • Multiplayer • Tournaments • Ranking",
    // GameUI
    armor: "ARMOR",
    targets: "TARGETS",
    points: "POINTS",
    radar: "RADAR",
    item: "ITEM",
    charge: "CHARGE",
    nominal: "NOMINAL",
    alert: "ALERT",
    critical: "CRITICAL",
    powerUp: "POWER+",
    cryo: "CRYO",
    shield: "SHIELD",
    turbo: "TURBO",
    controls: "WASD: Move | Space: Shoot",
    controlsDesc: "Game controls",
    forward: "W - FORWARD",
    reverse: "S - REVERSE",
    left: "A - LEFT",
    right: "D - RIGHT",
    clickToAim: "CLICK TO AIM WITH MOUSE",
    speed: "SPEED",
    equipment: "EQUIPMENT",
    empty: "EMPTY",
    heading: "HEADING",
    cameraToggle: "Camera (C)",
    // WaveComplete
    waveCompleteTitle: "WAVE {wave} COMPLETE!",
    finalVictory: "VICTORY!",
    enemiesEliminated: "Enemies eliminated",
    pointsLabel: "Score",
    remainingLife: "Remaining health",
    reward: "Reward",
    nextWaveLabel: "Next wave",
    continueBtn: "Continue →",
    restartFromBeginning: "Restart from Beginning",
    hpRestored: "+30 HP restored between waves",
    backToMenu: "Back to Menu",
    // Wave difficulty
    difficultyEasy: "Easy",
    difficultyMedium: "Medium",
    difficultyHard: "Hard",
    difficultyVeryHard: "Very Hard",
    difficultyExtreme: "Extreme",
    difficultyInsane: "Insane",
    difficultyImpossible: "Impossible",
    // PauseOverlay
    gamePaused: "Game Paused",
    storyPaused: "Story Paused",
    youPaused: "You paused the game",
    playerPaused: "{name} paused the game",
    anotherPlayerPaused: "Another player paused",
    tapToContinue: "Tap to continue",
    gamepadPauseHint: "Press A to resume • B or Start to return",
    restart: "Restart",
    restartCurrentRoom: "Restart Current Room",
    pauseControls: "ESC or P to pause/unpause",
    confirmQuit: "Tap again to quit",
    confirmRestart: "Tap again to restart",
    retryRun: "Try again",
    retryRunDesc: "Same tank, fresh run",
    changeMode: "Change mode or tank",
  },

  tank: {
    equipped: "Equipped",
    chooseTank: "Choose Tank",
    roguelikeMode: "Roguelike Mode - Terrain changes every 5 waves",
    speed: "Speed",
    damage: "Damage",
    health: "Health",
    unlocked: "Unlocked",
    locked: "Locked",
    startBattle: "Start Battle!",
    family: "Lineage",
    familyPredator: "Predators",
    familyColossus: "Colossi",
    familyVenom: "Venomous",
    familyStorm: "Storms",
    familyPhantom: "Phantoms",
    familyBulwark: "Bulwarks",
    familyCosmic: "Cosmics",
    familyMythic: "Mythics",
    familyDescPredator: "Swift scouts that hunt at full sprint",
    familyDescColossus: "Slow fortresses with colossal armor",
    familyDescVenom: "Hunters of constant, precise pressure",
    familyDescStorm: "Devastating long-range artillery",
    familyDescPhantom: "Infiltrators that vanish from enemy radar",
    familyDescBulwark: "Gem-armored tanks with living defense",
    familyDescCosmic: "Orbital control and stellar precision",
    familyDescMythic: "Living legends with signature power",
    special: "Power",
    specialElite: "Amplified elite power",
    specialShield: "Reactive Armor",
    specialStealth: "Active Camo",
    specialRegen: "Regeneration",
    specialExplosive: "Explosive Rounds",
    specialPiercing: "Piercing Rounds",
    specialHoming: "Homing Rounds",
    specialRebirth: "Phoenix Rebirth",
    specialBurn: "Dragon's Breath",
    specialDescShield: "Reduces all incoming damage",
    specialDescStealth: "Hold fire for a few seconds to vanish from distant enemies' radar",
    specialDescRegen: "Regenerates hull after a few seconds without taking damage",
    specialDescExplosive: "Impacts deal area damage around the target",
    specialDescPiercing: "Projectiles pierce through enemies in a line",
    specialDescHoming: "Projectiles steer toward the nearest enemy",
    specialDescRebirth: "When destroyed, rises from the ashes once with part of its hull",
    specialDescBurn: "Your shots set the target ablaze, dealing damage over time",
    mastery: "Mastery",
    masteryProgress: "{kills} kills — {next} to next level",
    masteryMax: "Max mastery — {kills} kills",
    speedShort: "Speed",
    damageShort: "Damage",
    healthShort: "Health",
    selected: "Selected",
    select: "Select",
    tapToView: "Tap to view",
    dragToRotate: "Drag to rotate in 3D",
    availableToBuy: "🔒 Available ({count})",
    skinLabel: "🎨 Skin: {name}",
    defaultSkin: "Default",
    activeSkinSolo: "Active skin (solo)",
    noSkin: "No skin (default)",
    unequipSkin: "Unequip skin",
    activeEffectSolo: "Active effect (solo)",
    noEffect: "No effect",
    unequipEffect: "Unequip effect",
    skinIncompatible: "Equipped skin \"{skin}\" is not compatible with {tank}. The preview falls back to the default look.",
  },

  multiplayer: {
    stepRoom: "Room",
    stepArena: "Arena",
    stepWait: "Wait",
    stepOf: "Step {n} of {total}",
    createRoomDesc: "Pick the arena and invite friends",
    joinRoomDesc: "Use the code a friend sent you",
    addBot: "+1 Bot",
    copyCode: "Copy",
    createRoom: "Create Room",
    joinRoom: "Join Room",
    join: "Join",
    roomCode: "Room Code",
    enterCode: "Enter code",
    waiting: "Waiting for players...",
    playersReady: "{count}/{total} players ready",
    startGame: "Start Game",
    leave: "Leave",
    copied: "Copied!",
    shareCode: "Share the code with your friends",
    // Lobby
    title: "MULTIPLAYER",
    subtitle: "Play with your friends",
    victoryTitle: "🏆 VICTORY!",
    defeatTitle: "💀 DEFEAT",
    playAgain: "Play Again",
    winner: "Winner",
    exit: "← Exit",
    shareWhatsApp: "Share on WhatsApp",
    selectedArena: "Selected Arena",
    yourTank: "Your Tank",
    yourColor: "Your Color",
    playersCount: "Players ({count}/4)",
    you: "(you)",
    host: "Host",
    gameMode: "Game Mode",
    elimination: "Elimination",
    eliminationDesc: "Last one standing",
    deathmatch: "Deathmatch",
    deathmatchDesc: "Kills to win",
    timeLimit: "Time",
    timeLimitDesc: "Most kills in time",
    livesMode: "Lives",
    livesModeDesc: "Limited lives",
    battleRoyale: "Battle Royale",
    battleRoyaleDesc: "Shrinking zone, last one standing",
    battleRoyaleHint: "The electric zone shrinks in phases and deals growing damage to anyone caught outside. No respawns: the last tank standing wins. Each elimination speeds up the next zone phase.",
    brAliveCount: "{count} alive",
    brZoneShrinking: "ZONE SHRINKING!",
    brZoneNextShrink: "Zone shrinks in {seconds}s",
    killsToWin: "Kills to Win",
    timeLimitMinutes: "Time Limit (minutes)",
    livesPerPlayer: "Lives per Player",
    damage: "Damage",
    speed: "Speed",
    health: "Health",
    waitingPlayers: "Waiting for players...",
    closeRoom: "🚫 Close Room",
    matchRules: "Match Rules",
    waitingHost: "Waiting for host to start the game...",
    leaveRoom: "← Leave Room",
    reconnecting: "Reconnecting...",
    chooseArena: "Choose Arena",
    chooseArenaDesc: "This will be the arena for all players",
    arenaLarge: "Large Arena",
    arenaMedium: "Medium Arena",
    arenaStandard: "Standard Arena",
    random: "Random",
    randomOnCreate: "Randomized when creating room",
    randomArena: "🎲 Random when creating room",
    yourName: "Your name",
    nameFromProfileHint: "Profile alias is used automatically. To change it, edit your profile.",
    nextChooseArena: "Next: Choose Arena →",
    connectionLost: "Connection lost",
    tryingToReconnect: "Trying to reconnect to previous room...",
    tryingReconnect: "Trying to reconnect to previous room...",
    disconnectedFromMatch: "You were disconnected from a match.",
    reconnect: "Reconnect",
    ignore: "Ignore",
    waitingMorePlayers: "Waiting for more players",
    loadingArena: "Loading arena...",
    matchStarting: "The battle is about to begin!",
    countdownGo: "GO!",
    creatingRoom: "Creating room...",
    joining: "Joining...",
    connectingToServer: "Connecting to server...",
    connectingServer: "Connecting to server...",
    playersAndBots: "Players and Bots",
    maxPlayersLabel: "Maximum Players (2-16)",
    botsFillSlots: "Bots (fill empty slots)",
    none: "None",
    botDifficulty: "Bot Difficulty",
    botEasy: "Easy",
    botNormal: "Normal",
    botHard: "Hard",
    autoFillBots: "Auto-fill Bots",
    removeBotsOnJoin: "Remove Bots on Join",
    adaptiveBots: "Adaptive Bots",
    tankSpecials: "Tank Powers",
    tankSpecialsHint: "Lineage powers (shield, stealth, regen...) apply in this room. Turn off for a fully neutral arena.",
    itemsAndPowerups: "Items and Power-ups",
    enableAllItems: "Enable All",
    disableAllItems: "Disable All",
    noItemsEnabledWarning: "No items enabled! Match will run without power-ups.",
    botStatusSummary: "Humans: {humans} | Active bots: {activeBots} | Configured: {configuredBots} | Projected total: {projected}/{max}",
    botFineTuning: "Fine bot tuning",
    modeDeathmatchWithKills: "Deathmatch ({kills} kills)",
    modeTimedWithMinutes: "{minutes}min",
    modeLivesWithCount: "{lives} lives",
    spectatorModeTitle: "Spectator Mode",
    spectatingPlayer: "Watching {player}",
    spectatorGenericPlayer: "Player",
    nextSpectator: "Next spectator",
    exitRoom: "Leave room",
    alivePlayersTitle: "Alive Players",
    errorOnlyHostRules: "Only the host can change match rules",
    errorRulesWaitingOnly: "Match rules can only be changed before the game starts",
    errorNeedTwoPlayers: "At least 2 players (humans + bots) are required to start",
    errorPlayersNotReady: "Not all players are ready",
    errorOnlyHostResume: "Only the host or the player who paused can resume the game",
    errorColorInUse: "Color is already in use by another player",
    errorOnlyHostRestart: "Only the host can restart",
    errorGameNotFinished: "Game has not finished yet",
    errorConnectFailed: "Failed to connect to server",
    errorCreateRoomFailed: "Failed to create room",
    errorRoomNotFound: "Room not found",
    myFriends: "My Friends",
    goToLobby: "Go to Lobby",
    backToMenu: "Exit to Menu",
  },

  br: {
    victory: "VICTORY!",
    champion: "Battle Royale Champion",
    position: "Position",
    kills: "Kills",
    players: "Players",
    score: "Score",
    reward: "REWARD",
    chips: "Chips",
    claimReward: "CLAIM PRIZE",
    rewardClaimed: "Prize Claimed!",
    totalChips: "Total: {count} chips",
    backToMenu: "BACK TO MENU",
    itemsNote: "Items become starting gear for your next match",
    eliminated: "Eliminated",
    ofPlayers: "of {count} players",
    spectate: "Spectate ({count})",
    menu: "Menu",
    alive: "{count} alive",
    zone: "Zone: {size}m",
    exit: "Exit",
    winner: "WINNER",
    nextTarget: "Next",
    matchOver: "Match over",
    noSurvivors: "No survivors",
    playAgain: "PLAY AGAIN",
    itemShield: "Extra Shield",
    itemIntense: "Intense Power",
    itemFreeze: "Freeze",
    itemHeal: "Med Kit",
    itemMystery: "Mystery Box",
  },
  friends: {
    title: "My Friends",
    myFriends: "My Friends",
    matchesTogether: "Matches Together",
    total: "Total",
    victories: "Victories",
    defeats: "Defeats",
    winRate: "Win Rate",
    yourKills: "Your Kills",
    yourDeaths: "Your Deaths",
    kd: "K/D",
    asAllies: "As Allies",
    matches: "Matches",
    estimatedMovement: "Estimated Movement",
    distanceInMatches: "Distance in matches together",
    history: "History",
    firstMatch: "First match",
    lastMatch: "Last match",
    totalSessions: "Total sessions",
    generalStats: "General Statistics",
    list: "List",
    yourJourney: "Your Journey",
    knownPlayers: "Known Players",
    multiplayerMatches: "Multiplayer Matches",
    performance: "Performance",
    totalCombat: "Total Combat",
    kills: "Kills",
    deaths: "Deaths",
    totalDistance: "Total Distance Traveled",
    distanceEstimate: "Estimate based on tank movement",
    recentMatches: "Recent Matches",
    noFriendsYet: "No friends yet",
    playMultiplayer: "Play multiplayer matches to start meeting other players!",
    recent: "Recent",
    distance: "Distance",
  },

  victory: {
    title: "🏆 VICTORY!",
    defeatTitle: "💀 DEFEAT",
    yourStats: "Your Statistics",
    kills: "Kills",
    deaths: "Deaths",
    finalScore: "Final Score",
    playAgain: "Play Again",
    goToLobby: "Go to Lobby",
    backToMenu: "Back to Menu",
    won: "You won the match!",
    winner: "Winner",
  },

  options: {
    title: "⚙️ Options",
    sound: "Sound",
    music: "Music",
    sfx: "Sound Effects",
    vibration: "Vibration",
    language: "Language",
    graphics: "Graphics",
    low: "Low",
    medium: "Medium",
    high: "High",
    notifications: "Notifications",
    reset: "Reset Progress",
    resetConfirm: "Are you sure? All your progress will be lost.",
    // Expanded options
    mute: "Mute",
    active: "Active",
    disabled: "Disabled",
    generalVolume: "General Volume",
    effects: "Effects",
    maxFps: "Max FPS",
    camera: "Camera",
    cameraTop: "Top",
    cameraAngled: "Angled",
    cameraFirstPerson: "1st Person",
    aiDifficulty: "AI Difficulty",
    // Haptics
    haptics: "Haptic Feedback",
    notSupported: "Not supported",
    hapticIntensity: "Intensity",
    testVibration: "Test Vibration",
    // Voice Narration
    voiceNarration: "Voice Narration",
    voiceNarrationDesc: "Enable voice narration in story mode dialogues. Requires additional audio downloads.",
    voiceVolume: "Voice Volume",
    beta: "Beta",
    // Controls
    controls: "Controls",
    controlsDesc: "Gamepad, touch, mapping",
    gamepadConnected: "Controller Connected",
    noGamepad: "No Controller Detected",
    connectGamepadHint: "Connect an Xbox, PlayStation, or Bluetooth controller",
    mobileCompatibility: "Mobile Compatibility",
    mobileAndroid: "Paired Bluetooth controllers work automatically",
    mobileIOS: "Limited support (MFi controllers only)",
    mobileMapping: "Automatic via browser Gamepad API",
    visualFeedback: "Visual Feedback (Test your controller)",
    leftStick: "Left Stick",
    rightStick: "Right Stick",
    buttonsPressed: "Buttons Pressed",
    noButtonPressed: "No button pressed",
    controlMapping: "Control Mapping",
    movement: "Movement",
    shoot: "Shoot",
    useItem: "Use Item",
    astra: "Astra (Story)",
    astraHint: "when charged",
    jump: "Jump",
    dash: "Dash",
    pause: "Pause",
    touch: "Touch",
    touchHint: "On-screen buttons",
    gamepadSettings: "Controller Settings",
    deadzone: "Dead Zone",
    deadzoneHint: "Adjust if the tank moves on its own",
    sensitivity: "Sensitivity",
    invertY: "Invert Y Axis",
    invertYHint: "Inverts the vertical axis of the stick",
    gamepadVibration: "Controller Vibration",
    hideVirtualControls: "Hide virtual controls when controller connected",
    unifiedControlsInfo: "All modes (Solo, Story, Battle Royale) use the same unified controls.",
    virtualJoystick: "Virtual Joystick",
  },

  difficulty: {
    easy: "Easy",
    easyDesc: "Enemies are slower and do less damage. Perfect for beginners.",
    normal: "Normal",
    normalDesc: "Balanced experience. The standard game mode.",
    hard: "Hard",
    hardDesc: "Smarter enemies with more health and damage. Good luck!",
    extreme: "Extreme",
    extremeDesc: "Maximum challenge. Only for the bravest!",
  },

  graphics: {
    title: "Graphics",
    subtitle: "Quality, shadows, resolution",
    quality: "Quality",
    qualityLow: "Low",
    qualityMedium: "Medium",
    qualityHigh: "High",
    qualityDesc: "Automatically adjusts explosions, lighting and shadows",
    explosions: "Explosions",
    advancedExplosions: "Advanced Explosions",
    advancedExplosionsDesc: "Extra particles and light effects",
    explosionLights: "Explosion Lights",
    shockwave: "Shockwave",
    lighting: "Lighting",
    advancedLighting: "Advanced Lighting",
    softShadows: "Soft Shadows",
    postProcessing: "Neon Bloom (Post-processing)",
    dynamicResolution: "Dynamic Resolution (auto)",
    shadowQuality: "Shadow Quality",
    effects: "Effects",
    screenShake: "Screen Shake",
    screenShakeDesc: "Camera shake on explosions",
    performance: "Performance",
    dynamicOptimization: "Dynamic Optimization",
    dynamicOptimizationDesc: "Auto-adjusts effects to maintain 30+ FPS",
    renderBackend: "Render Engine",
    webgpuUnavailable: "WebGPU unavailable",
    webgpuActive: "Experimental WebGPU active - better performance",
    fpsTarget: "FPS Target",
    resolution: "Resolution",
    fpsMonitor: "FPS Monitor",
    fpsMonitorDesc: "Draggable monitor during gameplay",
    tensionMonitor: "Music Tension Monitor",
    tensionMonitorDesc: "Shows adaptive music intensity level",
    bottomBar: "Bottom Bar",
    bottomBarDesc: "Bar with profile, coins and notifications",
    advancedSettings: "Advanced Settings",
    advancedSettingsDesc: "Detailed performance and effects adjustments",
    interfaceSection: "Interface",
    quickOptions: "Quick Options",
    autoDetectReset: "Reset to recommended FPS and graphics (auto-detect)",
    recommended: "RECOMMENDED",
    motion: "Interface motion",
    motionDesc: "Menu transitions, glows and animations. \"Automatic\" follows your system preference.",
    motionAuto: "Automatic",
    motionFull: "Full",
    motionReduced: "Reduced",
    activeEffects: "Active effects:",
    autoReducedForFps: "(automatically reduced to keep the FPS)",
    reducedMotionActive: "reduced motion",
  },

  rewards: {
    daily: "Daily Rewards",
    dailyDesc: "Come back every day to earn prizes!",
    claim: "Claim",
    claimed: "Claimed",
    streak: "Streak",
    day: "Day",
    comeBackTomorrow: "Come back tomorrow!",
    // DailyRewards expanded
    week1: "Week 1",
    week2: "Week 2",
    week3: "Week 3",
    week4: "Week 4",
    megaFinal: "Mega Final",
    enterDaily: "Enter every day!",
    record: "Record",
    currentDay: "Current Day",
    collect: "COLLECT!",
    nextSpecialReward: "Next special reward",
    daysLeft: "Days left",
    monthlyProgress: "Monthly Progress",
    rewardCollected: "Reward collected!",
    comeBackTomorrowMore: "Come back tomorrow for more",
    // New labels
    title: "🎁 Rewards",
    sequence: "Streak",
    xpBoost: "XP Boost!",
    chipsBoost: "Chips Boost!",
    days: "days",
    collectReward: "Collect Reward!",
    daysInRow: "days in a row!",
    thisMonth: "This Month",
    pending: "Pending",
  },

  battlePass: {
    noActiveSubscription: "No active subscription",
    title: "Battle Pass",
    subtitle: "Season 1 • Missions • Exclusive Rewards",
    free: "Free",
    premium: "Premium",
    level: "Level",
    xp: "XP",
    unlock: "Unlock",
    getPremium: "Get Premium",
    // BattlePassView expanded
    mostPopular: "MOST POPULAR",
    choosePlan: "Choose Your Plan",
    allPlansInclude: "All plans include the Premium Battle Pass!",
    cancelAnytime: "Cancel anytime. Secure payment via Stripe.",
    collectReward: "Collect Reward",
    rewardCollected: "✓ Reward collected",
    passActive: "✓ PASS ACTIVE",
    timeRemaining: "Time Remaining",
    activatePremium: "Activate Premium Pass",
    choosePlanDesc: "Choose Bronze, Silver or Gold Pass",
    missions: "📋 Missions",
    dailyMissions: "☀️ Daily Missions",
    dailyMissionsReset: "(Resets at midnight)",
    weeklyMissions: "📅 Weekly Missions",
    weeklyMissionsReset: "(Resets on Mondays)",
    loadingMissions: "Loading missions...",
    // Subscription plans
    bronzePass: "Bronze Pass",
    silverPass: "Silver Pass",
    goldPass: "Gold Pass",
    perMonth: "/month",
    crystalsPerDay: "crystals/day",
    chipsPerDay: "chips/day",
    xpAndChipsBonus: "+{percent}% XP and chips",
    noAds: "No ads",
    extraDailyMission: "+{count} daily mission",
    exclusiveSkin: "Monthly exclusive skin",
    priorityQueue: "Priority queue",
    subscribe: "Subscribe",
    crystalsValue: "${value} worth of crystals!",
    collect: "Collect",
    collected: "✓ Collected",
    season: "Season",
    ofLevel: "of",
    backToMenu: "← Back to Menu",
    rewards: "🎁 Rewards",
    // Subscription management
    manageSubscription: "Manage Subscription",
    managePayment: "Manage Payment",
    cancelSubscription: "Cancel Subscription",
    reactivateSubscription: "Reactivate Subscription",
    subscriptionActive: "Subscription Active",
    subscriptionCanceled: "Subscription Canceled",
    expiresAt: "Expires on {date}",
    confirmCancel: "Are you sure you want to cancel? You'll still have access until the end of the current period.",
    reactivate: "Reactivate Subscription",
    cancelsPeriodEnd: "Cancels at period end",
    renewsAt: "Renews on {date}",
    daysRemaining: "Days remaining",
    crystalsReceived: "Crystals received",
    subscriptionEnding: "Your subscription ends on {date}",
    supportContact: "Questions? support@rocketanks.com",
    active: "Active",
    canceling: "Canceling",
  },

  creators: {
    title: "🎬 Creators",
    enterCode: "Enter a creator code",
    yourCode: "Your code",
    totalReferrals: "Total referrals",
    useCode: "Use Code",
    codeUsed: "Code already used!",
    invalidCode: "Invalid code",
    ownCode: "You cannot use your own code",
    share: "Share",
    copied: "Copied!",
    // CreatorCode expanded
    codeApplied: "{name}'s code applied! {discount}% discount.",
    activeCode: "Active Code",
    enterCreatorCode: "Enter creator code",
    apply: "Apply",
    becomeCreator: "Become a Creator",
    earnCommission: "Earn commission when your followers buy using your code",
    supportCreator: "Support your favorite creator",
    getDiscount: "and get discount on purchases",
    // CreatorDashboard
    creatorDashboard: "Creator Dashboard",
    yourCreatorCode: "Your Creator Code",
    commission: "commission",
    discountForFans: "discount for fans",
    creatorPoints: "Creator Points",
    pendingValidation: "Pending Validation",
    pendingValidationDesc: "Your registration is being reviewed by our team. You will receive a notification when approved. This usually takes up to 48 hours.",
    verifiedCreator: "Verified Creator",
    verifiedCreatorDesc: "Congratulations! You are an official Rocketanks creator. Your badge appears next to your name!",
    registerAsCreator: "Register as Creator",
    overview: "📊 Overview",
    points: "⭐ Points",
    referrals: "👥 Referrals",
    rewardsTab: "🎁 Rewards",
    // Dashboard Overview
    totalEarnings: "Total Earnings",
    totalUses: "Total Uses",
    totalReferralsLabel: "Total Referrals",
    withdrawEarnings: "Withdraw Earnings",
    minWithdrawal: "Minimum withdrawal: 50 crystals",
    topSupporters: "🏆 Top Supporters",
    tierBenefits: "Benefits",
    // Points Panel
    creatorPointsBalance: "Creator Points",
    totalEarnedPoints: "Total earned: {count} points",
    howToEarnPoints: "How to Earn Points",
    convertPoints: "Convert Points",
    convertPointsDesc: "Exchange your points for in-game rewards",
    crystals: "Crystals",
    chips: "Chips",
    convertPointsBtn: "Convert {count} Points",
    insufficientPoints: "Insufficient points (you have {count})",
    conversionHistory: "📜 Conversion History",
    // Referrals Panel
    activeReferrals: "Active (30 days)",
    inviteFriends: "Invite Friends!",
    inviteFriendsDesc: "Share your code and earn {points} points for each new player!",
    yourReferrals: "Your Referrals",
    yourReferralsDesc: "Players who used your code when signing up",
    noReferralsYet: "No referrals yet",
    shareCodeToStart: "Share your code to get started!",
    joinedOn: "Joined on {date}",
    // Rewards Panel
    exclusiveCreatorItems: "Exclusive Creator Items",
    exclusiveCreatorItemsDesc: "Unlock items that only creators can have!",
    creatorSkin: "Creator Skin",
    creatorSkinDesc: "Exclusive for verified creators",
    creatorEffect: "Creator Effect",
    creatorEffectDesc: "Exclusive special trail",
    pixRewards: "PIX Rewards",
    pixRewardsDesc: "We're working on ways for you to receive real money!",
    pixWithdraw: "PIX Withdraw",
    pixWithdrawDesc: "Convert your earnings into real money",
    pixMonthlyRaffle: "Monthly PIX Raffle",
    pixMonthlyRaffleDesc: "Active creators compete for cash prizes",
    comingSoon: "COMING SOON",
    stayTuned: "Stay tuned for news! The more you promote the game, the greater your rewards will be.",
    confirmRedemption: "Confirm Redemption",
    confirmRedemptionDesc: "You are about to redeem an exclusive creator item.",
    cancel: "Cancel",
    confirm: "Confirm",
    // Register Dialog
    registerAsCreatorTitle: "Register as Creator",
    registerAsCreatorDesc: "Fill in your social media for review. After approval, you will receive a verified creator badge!",
    validationNotice: "Your registration will be manually reviewed within 48 hours. Approved creators receive a special badge!",
    followers: "followers",
    fillOneSocialMedia: "Fill in at least one social media",
    registrationError: "Registration error",
    submitForReview: "Submit for Review",
  },

  referral: {
    title: "Referral",
    yourCode: "Your code",
    copied: "✓ Copied!",
    copy: "📋 Copy",
    share: "📤 Share",
    friendsUseCode: "When friends use your code, you both get",
    bothGet: "100 ⚡ chips",
    friendCode: "Friend's code?",
    alreadyUsedCode: "✓ You already used a referral code",
    redeemAndEarn: "🎁 Redeem and earn 100 ⚡",
    enterCode: "Enter a code!",
    codePlaceholder: "Ex: TANK-ABC12",
    errorUsingCode: "Error using code",
    connectionError: "Connection error. Please try again.",
    shareMessage: "🎮 Play Rocketanks with me! Use my code {code} and get 100 ⚡ free chips!\n\nhttps://rocketanks.nagentes.com",
  },

  credits: {
    title: "Credits",
    developer: "Developer",
    mainTester: "Main Tester",
    sponsors: "Sponsors",
    sponsorsDesc: "The biggest Rocketanks supporters appear here!",
    comingSoon: "Coming soon...",
    buyCrystals: "Buy crystals and see your name here!",
    allRightsReserved: "All rights reserved",
  },

  ranked: {
    profile: "Ranked Profile",
    trophies: "trophies",
    victories: "Victories",
    defeats: "Defeats",
    winRate: "Win Rate",
    highestTrophies: "Highest Trophies",
    currentStreak: "Current Streak",
    bestStreak: "Best streak: {count} victories",
    recentMatches: "Recent Matches",
    classification: "Classification",
    global: "Global",
    myLeague: "My League",
    friends: "Friends",
    yourPosition: "Your Position",
    noPlayersFound: "No players found",
  },

  tournaments: {
    title: "🏆 Tournaments",
    subtitle: "Competitions with prizes",
    selectToJoin: "Select a tournament to join",
    noTournamentAvailable: "No tournament available",
    newTournamentSoon: "A new tournament will be created soon",
    players: "players",
    open: "Open",
    almostFull: "Almost full",
    join: "Join",
    leaveTournament: "Leave Tournament",
    entry: "Entry",
    free: "Free",
    firstPlace: "1st Place",
    duration: "Duration",
    minutes: "min",
    tournaments: "Tournaments",
    victories: "Victories",
    top3: "Top 3",
    prizes: "Prizes",
    registering: "Registering",
    inProgress: "In progress",
    completed: "Completed",
    waitingPlayers: "Waiting for players...",
    round: "Round",
    final: "Final",
    participants: "Participants",
    yourNextMatch: "Your next match",
    place: "place",
    bronze: "Bronze",
    silver: "Silver",
    gold: "Gold",
    diamond: "Diamond",
    unknownError: "Unknown error",
    tournamentsPlayed: "Tournaments played",
    tournamentsWon: "Tournaments won",
    top3Rate: "Top 3 rate",
    totalPrizeWon: "Total prizes won",
  },

  clan: {
    title: "⚔️ Clan",
    subtitle: "Join other players",
    noClan: "You're not in a clan",
    noClanDesc: "Join a clan to participate in wars and earn rewards!",
    officialClans: "Official Clans",
    officialClansFree: "Official Clans (Free)",
    viewOfficialClans: "View Official Clans",
    searchClan: "Search Clan",
    createClan: "Create Clan",
    joinClan: "Join Clan",
    leaveClan: "Leave Clan",
    leader: "Leader",
    coLeader: "Co-Leader",
    elder: "Elder",
    member: "Member",
    promote: "Promote",
    demote: "Demote",
    kick: "Kick",
    agoMinutes: "{count}m ago",
    agoHours: "{count}h ago",
    agoDays: "{count}d ago",
    perWeek: "/week",
    noMessages: "No messages yet",
    clanMessage: "Clan message...",
    trophies: "Trophies",
    donations: "Donations",
    lastActive: "Last active",
    members: "Members",
    clanLevel: "Clan Level",
    clanWars: "Clan Wars",
    clanName: "Clan Name",
    clanTag: "Clan Tag",
    clanDescription: "Description",
    clanIcon: "Icon",
    createClanBtn: "Create Clan",
    searchPlaceholder: "Search by name or tag...",
    noClansFound: "No clans found",
    availableClans: "7 Clans Available",
    basedOnStoryMode: "Based on Story Mode",
    clanTypes: "Clan Types",
    publicClans: "Public",
    publicClansDesc: "Free, maintained by Admin",
    privateClans: "Private",
    privateClansDesc: "500💎 to create, members pay 500💎",
    official: "OFFICIAL",
    all: "All",
    beginners: "Beginners",
    story: "Story",
    freeOfficialClansInfo: "Free! Official clans have no entry fee.",
    adminRevenueInfo: "Admin receives 5% of earnings based on member XP",
    privateType: "Private",
    publicType: "Public",
    publicOfficialOnly: "Official clans only",
    clanNamePlaceholder: "Ex: Invincible Tanks",
    clanTagPlaceholder: "Ex: TANK",
    clanDescriptionPlaceholder: "Describe your clan...",
    costSummary: "Cost Summary",
    createClanCost: "Create clan:",
    memberFee: "Fee per member:",
    free: "Free",
    creating: "Creating...",
    info: "Info",
    wins: "Wins",
    level: "Level",
    noDescription: "No description",
    warsComingSoon: "Coming soon! Battle other clans for epic rewards.",
    inDevelopment: "In development",
  },

  replays: {
    title: "🎬 Replays",
    subtitle: "Rewatch your best matches",
    noReplays: "No saved replays",
    noReplaysDesc: "Your matches will be saved automatically",
    lastMatch: "Last Match",
    lastMatchDesc: "One replay per game mode, auto-saved",
    importReplay: "Import Replay",
    exportReplay: "Export",
    shareReplay: "Share",
    deleteReplay: "Delete",
    play: "Play",
    pause: "Pause",
    survival: "Survival",
    multiplayer: "Multiplayer",
    story: "Story",
    duration: "Duration",
    players: "Players",
    wave: "Wave",
    views: "Views",
    importTitle: "Import Replay",
    importPlaceholder: "Paste the replay code here...",
    import: "Import",
    codeCopied: "Code copied!",
    // Viewer
    viewer: "Replay Viewer",
    speed: "Speed",
    stop: "Stop",
    resume: "Resume",
    replayEnded: "Replay ended",
    dragTimeline: "Drag to navigate",
    snapshot: "Snapshot",
    events: "Events",
    // Tags
    tagMultiKill: "Multi-Kill",
    tagClutch: "Clutch",
    tagPerfectWave: "Perfect Wave",
    tagLongGame: "Long Game",
    tagHighScore: "High Score",
    tagFlawless: "Flawless",
    // Toast
    shareCopied: "Share link copied!",
    exportCopied: "Replay exported and copied!",
    shareError: "Failed to share replay",
    exportError: "Failed to export replay",
    deleteConfirm: "Delete this replay?",
  },

  subscription: {
    errorTitle: "Something went wrong",
    closeError: "Close",
    title: "⭐ Subscription",
    subtitle: "Unlock exclusive benefits",
    free: "Free",
    freePlan: "Free Plan",
    bronzePass: "Bronze Pass",
    silverPass: "Silver Pass",
    goldPass: "Gold Pass",
    perMonth: "/month",
    dailyCrystals: "crystals/day",
    dailyChips: "chips/day",
    xpBonus: "+{percent}% XP",
    noAds: "No ads",
    exclusiveSkin: "Monthly exclusive skin",
    priorityQueue: "Priority queue",
    dailyRewards: "Daily rewards",
    available: "Available!",
    claimedToday: "Claimed today",
    claimNow: "Claim Now",
    claiming: "Claiming...",
    claimed: "Claimed: {crystals} crystals + {chips} chips!",
    manageSubscription: "Manage Subscription",
    upgrade: "Upgrade",
    viewPlans: "View Plans",
    cancelSubscription: "Cancel Subscription",
    canceledAt: "Cancels on {date}",
    months: "months",
    month: "month",
    totalReceivedMonth: "Total received this month:",
    totalCrystals: "crystals",
    totalChips: "chips",
    crystalsValue: "${value} worth of crystals!",
    unlockDailyRewards: "Unlock exclusive daily rewards!",
    // SubscriptionCard dialog
    plansTitle: "Subscription Plans",
    plansDescription: "Choose the ideal plan for you and receive exclusive daily rewards!",
    mostPopular: "Most Popular",
    receiveDaily: "Receive every day:",
    currentPlan: "Current Plan",
    processing: "Processing...",
    lowerPlan: "Lower Plan",
    subscribe: "Subscribe",
    loginToSubscribe: "Log in to subscribe",
    confirmCancelMessage: "Are you sure you want to cancel your subscription? You will still have access until the end of the current period.",
    reactivateSubscription: "Reactivate Subscription",
    paymentSecure: "Secure payment via Stripe",
    cancelNoFee: "Cancel anytime, no fee",
    rewardsAccumulate: "Rewards accumulate even on days without login",
    instantUpgrade: "Instant upgrade, prorated difference charged",
    claimedRewards: "✓ Claimed: {crystals} crystals + {chips} chips!",
    // Dynamic feature templates
    featureBattlePassPremium: "Battle Pass Premium",
    featureCrystalsDaily: "crystals/day",
    featureChipsDaily: "chips/day",
    featureXpChipsBonus: "XP and chips",
    featureNoAds: "No ads",
    featureExclusiveSkins: "Exclusive skins",
    featurePriorityQueue: "Priority queue",
    featureExtraMissions: "extra daily mission(s)",
    featureFreeChipsDaily: "daily chips",
    featureBasicAccess: "Basic game access",
    valueInCrystals: "${value} worth of crystals!",
  },

  genAI: {
    // Personalities
    aggressive: "Aggressive 🔥",
    defensive: "Defensive 🛡️",
    strategic: "Strategic 🧠",
    player: "Player",
    // Victory messages
    battleComplete: "Battle Complete!",
    youWon: "You Won!",
    youLost: "You Lost!",
    aiVsAiMode: "AI vs AI Mode",
    playerVsAiMode: "Player vs AI Mode",
    model: "Model",
    winningModel: "Winning Model",
    menu: "Menu",
    playAgain: "Play Again",
  },

  rankedProfile: {
    title: "Ranked Profile",
    trophies: "trophies",
    trophiesToNextTier: "{count} trophies to {tier}",
    victories: "Victories",
    defeats: "Defeats",
    winRate: "Win Rate",
    highestTrophies: "Highest Trophies",
    currentStreak: "Current Streak",
    bestStreak: "Best streak: {count} wins",
    recentMatches: "Recent Matches",
    winsLabel: "wins",
  },

  marketplace: {
    explore: "Explore",
    create: "Create",
    myItems: "My Items",
    history: "History",
    createBuySell: "Create, buy and sell unique items!",
    sale: "Sale",
    auction: "Auction",
    trade: "Trade",
    free: "Free",
    // New labels
    title: "Marketplace",
    subtitle: "Buy, sell and trade items with other players",
    searchItems: "Search items...",
    noItemsFound: "No items found",
    adjustFilters: "Try adjusting the filters",
    all: "All",
    skins: "Skins",
    effects: "Effects",
    tanks: "Tanks",
    badges: "Badges",
    backToMenu: "← Back to Menu",
    feeInfo: "Fee",
    // Tabs
    tabs: {
      browse: "Browse",
      flash: "Flash",
      bundles: "Bundles",
      create: "Create",
      myItems: "My Items",
      profile: "Profile",
      history: "History",
    },
    // Fees
    fees: {
      sale: "Sale",
      auction: "Auction",
      trade: "Trade",
      royalties: "Royalties",
      level: "Level",
      discount: "Discount",
    },
    // Content creation
    createContent: "🎨 Create Content",
    createContentDesc: "Create unique items to sell on the Marketplace",
    contentType: "Item Type",
    itemName: "Item Name",
    itemDescription: "Description (optional)",
    skinColor: "Color Skin",
    visualEffect: "Visual Effect",
    sticker: "Sticker",
    skinColorDesc: "Custom color for your tank",
    visualEffectDesc: "Unique trail or explosion",
    stickerDesc: "Decorative image (64x64)",
    colors: "Colors",
    gradient: "Gradient",
    primaryColor: "Primary Color",
    secondaryColor: "Secondary Color",
    preview: "Preview",
    uploadImage: "Sticker Image",
    clickToUpload: "Click to upload",
    clickToChange: "Click to change",
    imageRules: "PNG, JPG or WebP • Max 64x64px • Max 50KB",
    moderationWarning: "⚠️ Images are resized to 64x64 and go through moderation before being approved.",
    submitForApproval: "Submit for Approval",
    submitting: "Submitting...",
    submitted: "✓ Submitted!",
    itemSentModeration: "Item sent for moderation!",
    moderationNotify: "You will be notified when it's approved.",
    // Content rules
    contentRules: "📋 Content Rules:",
    originalOnly: "Original content only",
    noOffensive: "No offensive or inappropriate images",
    noLogos: "No trademarks or logos",
    moderationTime: "Moderation takes up to 24 hours",
    // My items tabs
    selling: "For Sale",
    sold: "Sold",
    pending: "Pending",
    // Listing status
    active: "Active",
    expired: "Expired",
    soldStatus: "Sold",
    // Transaction history
    totalEarned: "Total Earned",
    totalSpent: "Total Spent",
    noTransactions: "No transactions yet",
    soldTo: "Sold to {name}",
    boughtFrom: "Bought from {name}",
    // Listing details
    price: "Price",
    currentBid: "Current Bid",
    yourBid: "Your Bid (min: {min})",
    minBid: "min",
    buyNow: "Buy Now",
    placeBid: "Place Bid",
    processing: "Processing...",
    insufficientCrystals: "Insufficient Crystals",
    purchasesUnavailable: "Purchases unavailable",
    cancel: "Cancel",
    // Flash sales
    noFlashSales: "No Active Flash Sales",
    flashSalesDesc: "Flash Sales are limited-time offers with amazing discounts. Check back later!",
    endsIn: "Ends in",
    remaining: "{count} remaining of {total}",
    soldOut: "SOLD OUT",
    buy: "Buy",
    unavailable: "Unavailable",
    // Featured
    official: "✓ OFFICIAL",
    officialDesc: "Premium skins and effects • Official Rocketanks seller",
    viewItems: "View items →",
    createYourSkins: "✨ Create your own skins!",
    createYourSkinsDesc: "Customize colors, effects and earn royalties on sales",
    // Validation errors
    nameMinChars: "Name must have at least 3 characters",
    nameBannedWord: "Name contains prohibited term",
    invalidColor: "Invalid color (use #RRGGBB format)",
    imageRequired: "Image required for stickers",
    fileTooLarge: "File too large (max {size}KB)",
    invalidImage: "File must be an image",
    imageProcessError: "Error processing image",
    // Offer
    offer: "Offer",
    // Type badges
    saleType: "SALE",
    auctionType: "AUCTION",
    tradeType: "TRADE",
    // Telas de vendedor, pacotes e moderação
    itemSentForModeration: "Item sent for moderation!",
    willBeNotified: "You'll be notified once it's approved.",
    rule1: "Only original content or content you have the rights to",
    rule2: "Nothing offensive, overly violent or unsuitable for minors",
    rule3: "No third-party brands, logos or characters",
    rule4: "Items that break the rules are removed without refund",
    noItemsForSale: "You have no items for sale",
    noItemsSold: "No items sold yet",
    noItemsPending: "No items awaiting approval",
    purchasedFrom: "Bought from {name}",
    purchasesDisabledWarning: "Marketplace purchases are unavailable on this account.",
    purchasesUnavailableShort: "Purchase unavailable",
    purchaseFailed: "The purchase couldn't be completed",
    bundlesAndCollections: "Bundles and collections",
    bundlesDesc: "Discounted item sets put together by creators",
    createBundle: "Create bundle",
    noBundles: "No bundles available",
    noBundlesDesc: "Check back later for new collections.",
    starterPack: "Starter pack",
    seasonal: "Seasonal",
    themed: "Themed",
    exclusive: "Exclusive",
    buyBundle: "Buy bundle",
    verificationSent: "Verification request sent!",
    verificationFailed: "Couldn't send the verification request",
    save: "Save",
    noDescription: "No description",
    edit: "Edit",
    followers: "Followers",
    sales: "Sales",
    sellerLevel: "Seller level",
    progressToLevel: "Progress to level {level}",
    xpToNextLevel: "{xp} XP to the next level",
    maxLevelReached: "Max level reached!",
    feeDiscount: "Fee discount",
    activeListings: "Active listings",
    auctions: "Auctions",
    bundles: "Bundles",
    creatorStats: "Creator stats",
    contentsCreated: "Content created",
    downloads: "Downloads",
    likes: "Likes",
    royaltiesEarned: "Royalties earned",
    requestVerification: "Request verification",
    verificationBenefits: "Verified sellers get a profile badge and more visibility in search. Every request is reviewed by the team.",
    creator: "Creator",
    creatorReq: "Publish original content on the Marketplace",
    artist: "Artist",
    artistReq: "Show a portfolio of skins and artwork",
    streamer: "Streamer",
    streamerReq: "Stream the game regularly",
  },

  shopEffects: {
    trail: "🔥 Trail",
    explosion: "💥 Explosion",
    shot: "⚡ Shot",
    aura: "✨ Aura",
    movement: "💨 Movement",
    special: "🌟 Special",
  },

  ui: {
    dailyRewards: "Daily Rewards",
    changeTank: "Change tank",
    summary: "Summary",
    battles: "Battles",
    wins: "Wins",
    kills: "Kills",
    winRate: "Win rate",
    touchToStart: "Touch to Start",
    starting: "Starting...",
    mobileControls: "Mobile: Left touch to move, right to shoot",
    desktopControls: "Desktop: WASD or arrows to move, Space to shoot",
    shopAndRewards: "Shop & Rewards",
    shop: "Shop",
    rewards: "Rewards",
    creators: "Creators",
    refer: "Refer",
    // Update notification
    newVersionAvailable: "New version available!",
    updateReady: "A Rocketanks update is ready to install.",
    updating: "Updating...",
    updateNow: "Update Now",
    later: "Later",
    // Error boundary
    somethingWentWrong: "🚀 Oops! Something went wrong",
    unknownError: "Unknown error",
    reloadPage: "Reload Page",
    technicalDetails: "Technical details (for developers)",
    screenLoadError: "Something went wrong while loading this screen. Please try again.",
    chips: "Chips",
    crystals: "Crystals",
    missions: "Missions",
    notifications: "Notifications",
    viewList: "List",
    viewCards: "Cards",
    backToMenu: "← Back to Menu",
    offline: "No internet connection",
    // Chapters
    comingSoon: "Coming Soon",
    replay: "Replay",
    play: "Play",
  },

  story: {
    missionFailed: "MISSION FAILED",
    missionFailedDesc: "Your tank was destroyed. Give it another shot!",
    tryAgain: "Try Again",
    backToChapters: "Back to Chapters",
    exitStory: "✕ Exit",
    tapToContinue: "Tap to continue",
    chapter: "CHAPTER {chapter}",
    mode: "Story Mode",
    // Story Mode page
    title: "STORY MODE",
    subtitle: "The story of Arthur Ren and KITSUNE-00",
    backToMenu: "← Back to Menu",
    prologueLabel: "Prologue",
    prologueTitle: "Prologue",
    prologueDesc: "Before Chapter 1: Arthur's awakening",
    // Chapters
    chapters: {
      awakening: "Awakening",
      awakeningDesc: "The encounter with KITSUNE-00",
      firstConflict: "First Conflict",
      firstConflictDesc: "Face Sergeant RØK",
      rescue: "Rescue",
      rescueDesc: "Protect the deserter engineer",
      iceStorm: "Ice Storm",
      iceStormDesc: "Commander Boreal's test",
      darkForest: "Dark Forest",
      darkForestDesc: "Face General Verdant",
      finalVolcano: "Final Volcano",
      finalVolcanoDesc: "The Heart of Arcadia",
      shadowsOfPast: "Shadows of the Past",
      shadowsOfPastDesc: "Fragmented Memories",
      iceAndSilence: "Ice and Silence",
      iceAndSilenceDesc: "The Fox Cemetery",
      theTraitor: "The Traitor",
      theTraitorDesc: "CORVUS-7 Awakens",
      prime: "PRIME",
      primeDesc: "The Final Confrontation",
    },
  },

  greetings: {
    morning: "Good morning!",
    afternoon: "Good afternoon!",
    evening: "Good evening!",
    lateNight: "Late night!",
    restMessage1: "It's getting late... one quick match then rest?",
    restMessage2: "Time to rest! Your body appreciates a good night's sleep.",
    restMessage3: "Still awake? Take care of your health, go to sleep!",
    firstBattle: "Ready for your first battle?",
    startingWell: "You're starting well! Keep it up.",
    keepGoing: "Don't give up! Victory is near.",
    firstWin: "First victory achieved! On to more!",
    moreWins: "{count} wins! You're improving.",
    gettingBetter: "{count} wins! You're a skilled commander.",
    skilled: "{count} wins! War veteran!",
    veteran: "{count} wins! Battle legend!",
    legend: "{count} wins! Supreme tank master!",
    master: "{count} enemies defeated! Unstoppable!",
    unstoppable: "{count} enemies! You're a war machine!",
    warMachine: "{count} enemies! You're a war machine!",
    richInChips: "Rich in chips! Have you unlocked all tanks?",
  },

  errors: {
    connectionFailed: "Connection failed",
    serverError: "Server error",
    timeout: "Timeout",
    invalidInput: "Invalid input",
    permissionDenied: "Permission denied",
    notFound: "Not found",
    cameraError: "Camera access error",
    cameraInUse: "Camera in use by another app",
    cameraNotFound: "No camera found on device",
    httpsRequired: "Security error. Make sure you're using HTTPS.",
  },

  auth: {
    tapToPlay: "🎮 Tap to Play",
    welcome: "Welcome!",
    chooseLoginMethod: "Choose how to sign in",
    loginWithGoogle: "Sign in with Google",
    loginWithDiscord: "Sign in with Discord",
    loginWithEmail: "Sign in with Email",
    comingSoon: "Coming soon",
    unavailable: "Unavailable",
    emailCodeSent: "📬 Enter the 6-digit code sent to your email.",
    emailCodePrompt: "We'll send a verification code to your email.",
    sendCode: "Send code →",
    sending: "⏳ Sending...",
    verifying: "⏳ Verifying...",
    confirmAndLogin: "✓ Confirm and Sign In",
    useAnotherEmail: "← Use another email",
    useAnotherAccount: "🔄 Use another account",
    completeProfile: "🎯 Complete your Profile",
    profileInfoNeeded: "We need some information to get started",
    email: "Email",
    fullName: "Full name",
    fullNamePrivate: "(private)",
    publicAlias: "Public alias",
    aliasChars: "(3-16 characters)",
    birthDate: "Birth date",
    yourName: "Your name",
    yourAlias: "your_alias",
    yourEmail: "your@email.com",
    saving: "⏳ Saving...",
    saveAndPlay: "🚀 Save and Play!",
    welcomeBack: "Welcome back,",
    playNow: "🎮 Play Now",
    starting: "⏳ Starting...",
    termsAgree: "I have read and agree to the",
    termsOfUse: "Terms of Use",
    privacyPolicy: "Privacy Policy",
    aliasInUse: "Alias is already in use or invalid",
    aliasInvalid: "Invalid alias",
    // Bio / Profile
    bio: "Bio",
    bioPlaceholder: "Tell us a bit about yourself...",
    clickToAddBio: "Click to add a bio...",
    change: "Change",
    // Validation errors
    invalidEmail: "Invalid email",
    fullNameMinLength: "Full name must be at least 3 characters",
    aliasMinLength: "Alias must be 3-16 characters (letters, numbers, _)",
    birthDateRequired: "Birth date is required",
    profileSaveFailed: "Failed to save profile",
    connectionError: "Connection error. Try again.",
  },

  admin: {
    title: "Admin Panel",
    loginError: "Login error",
    playerNotFound: "Player not found",
    announcementPlaceholder: "Type the announcement content here...",
    search: "Search",
    searchPlayer: "Search Player",
    wins: "Wins",
    systemSettings: "System Settings",
    refreshData: "Refresh data",
    settingLocked: "This setting is locked",
    dailyRewards: "Daily Rewards",
    successRate: "Success rate",
    playerUUID: "Player UUID",
    minAge: "Minimum age",
    maxAge: "Maximum age",
    resetMemoriesConfirm: "Are you sure you want to reset ALL AI memories?",
    genaiLearning: "AI Learning System",
    totalMemories: "Total Memories",
    totalMatches: "Total Matches",
    uniquePlayers: "Unique Players",
    topPlayers: "Top Players",
    recentMatches: "Recent Matches",
    resetPlayerMemory: "Reset player memory",
    resetAllMemories: "Reset all memories",
    noMemoriesYet: "No memories recorded yet",
    announcements: "Announcements",
    players: "Players",
    dashboard: "Dashboard",
    config: "Configuration",
  },
  
  legal: {
    // Common
    back: "Back",
    lastUpdated: "Last updated",
    contactUs: "Contact Us",
    generalSupport: "General Support",
    privacyEmail: "Privacy Email",
    companyInfo: "NAgentes Tecnologia LTDA • CNPJ: XX.XXX.XXX/0001-XX • Brazil",
    legalQuestions: "Legal Questions",
    introduction: "Introduction",
    
    // Terms of Use page
    termsTitle: "Terms of Use",
    termsSubtitle: "Your rules for a fair and fun experience",
    termsHeroTitle: "Game Rules",
    termsHeroDesc: "Terms and conditions for using Rocketanks.",
    termsSummary: "Summary",
    termsSummaryDesc: "What you need to know",
    
    // Terms intro
    termsWelcome: "Welcome to Rocketanks!",
    termsWelcomeDesc: "By creating an account or using Rocketanks, you agree to these Terms of Use. Please read carefully. If you disagree with any term, please do not use our services.",
    termsAgeTitle: "Age Rating",
    termsAgeDesc: "Rocketanks is rated for ages 10 and up. Players under 18 must have parental or legal guardian consent to play. In-app purchases require authorization from a responsible adult.",
    
    // Terms sections titles
    termsLicense: "License of Use",
    termsAccount: "Your Account",
    termsConduct: "Code of Conduct",
    termsPurchases: "Purchases and Virtual Currency",
    termsIntellectual: "Intellectual Property",
    termsAvailability: "Service Availability",
    termsLiability: "Limitation of Liability",
    termsPenalties: "Violations and Penalties",
    termsChanges: "Changes to Terms",
    termsLaw: "Applicable Law",
    
    // Terms License content
    termsLicenseDesc: "We grant you a limited, personal, non-exclusive, non-transferable and revocable license to use Rocketanks in accordance with these terms.",
    termsYouCan: "You can:",
    termsYouCanPlay: "Play for personal entertainment",
    termsYouCanPurchase: "Make in-app purchases to enhance your experience",
    termsYouCanCreate: "Create and share game content (streams, videos)",
    termsYouCanParticipate: "Participate in community and events",
    termsYouCannot: "You can NOT:",
    termsYouCannotModify: "Modify, hack or reverse engineer",
    termsYouCannotSell: "Sell or trade virtual items outside the game",
    termsYouCannotMultiple: "Create multiple accounts to gain advantage",
    termsYouCannotBots: "Use bots, cheats or third-party software",
    
    // Terms Account content
    termsAccountDesc: "You are responsible for maintaining your account security and for all activities performed on it.",
    termsAccountSecurity: "Security",
    termsAccountSecurityShare: "Do not share your account",
    termsAccountSecurityAuth: "Use secure authentication",
    termsAccountSecurityReport: "Report suspicious access",
    termsAccountName: "Your Name",
    termsAccountNameRespect: "Be respectful in your alias",
    termsAccountNameOffensive: "Nothing offensive or inappropriate",
    termsAccountNameChange: "We may change improper names",
    termsAccountWarning: "We reserve the right to suspend accounts that violate these terms.",
    
    // Terms Conduct content
    termsCondictDesc: "To maintain a healthy community, we expect everyone to follow these rules:",
    termsRespectPlayers: "Respect other players",
    termsRespectPlayersDesc: "Be polite in chat and during matches",
    termsPlayFair: "Play fair",
    termsPlayFairDesc: "Don't use exploits, bugs or cheats",
    termsReportViolations: "Report violations",
    termsReportViolationsDesc: "Help keep the community safe",
    termsNoToxicity: "No toxicity",
    termsNoToxicityDesc: "Harassment, hate speech, bullying = ban",
    termsNoSpam: "No spam",
    termsNoSpamDesc: "Advertising or flooding is not allowed",
    
    // Terms Purchases content
    termsCurrencies: "Crystals and Chips",
    termsCurrenciesDesc: "These are virtual currencies used to acquire items in the game. They have no real monetary value and cannot be exchanged for money.",
    termsPurchasesFinal: "All purchases are final and non-refundable, except where required by law",
    termsPricesChange: "Prices may change at any time",
    termsVirtualItems: "Virtual items belong to NAgentes and are licensed to you",
    termsNoRefundShutdown: "In case of game shutdown, unused currencies will not be refunded",
    termsConsumerProtection: "Consumer Protection:",
    termsConsumerProtectionDesc: "Players under 18 need parental authorization for purchases. We offer parental controls to limit spending.",
    
    // Terms Intellectual content
    termsIntellectualDesc: "Rocketanks, including all graphics, music, code, characters, names and design are property of NAgentes Tecnologia.",
    termsContentCreation: "Content Creation:",
    termsContentCreationDesc: "You may create videos, streams and content about Rocketanks, as long as:",
    termsContentNotOfficial: "You don't claim it's official content",
    termsContentNotMonetize: "You don't monetize in a way that deceives consumers",
    termsContentCredit: "You give credit to the original game",
    
    // Terms Availability content
    termsAvailabilityDesc: "We strive to keep Rocketanks always available, but we do not guarantee uninterrupted availability.",
    termsMaintenanceNotice: "Maintenance may occur with or without prior notice",
    termsServiceModify: "The service may be modified or terminated at any time",
    termsProgressLoss: "We are not responsible for progress loss in force majeure cases",
    
    // Terms Liability content
    termsLiabilityDesc: "Rocketanks is provided \"as is\". Within the limits permitted by law:",
    termsNoBugGuarantee: "We do not guarantee that the game will be bug-free or error-free",
    termsNoIndirectDamage: "We are not responsible for indirect or consequential damages",
    termsMaxLiability: "Our maximum liability is limited to the amount paid in the last 12 months",
    termsLiabilityImportant: "Important:",
    termsLiabilityImportantDesc: "These limitations do not affect rights you have by law that cannot be limited or excluded.",
    
    // Terms Penalties content
    termsPenaltiesDesc: "Violations of these terms may result in:",
    termsWarning: "Warning",
    termsWarningDesc: "First minor violation",
    termsSuspension: "Suspension",
    termsSuspensionDesc: "Temporary (1-30 days)",
    termsBan: "Ban",
    termsBanDesc: "Permanent without refund",
    termsPenaltiesSeverity: "Severity depends on the nature and frequency of the violation. Decisions are made at our discretion.",
    
    // Terms Changes content
    termsChangesDesc: "We may update these terms periodically. When we make significant changes:",
    termsChangesNotify: "We will notify you by email or in the app",
    termsChangesDate: "The \"last updated\" date will be modified",
    termsChangesAccept: "Continuing to use the game means accepting the new terms",
    
    // Terms Law content
    termsLawDesc: "These terms are governed by the laws of the Federative Republic of Brazil.",
    termsLawCourt: "Any dispute will be resolved in the competent courts of the city of São Paulo, SP.",
    termsFriendlyResolution: "Friendly Resolution:",
    termsFriendlyResolutionDesc: "Before any legal action, we encourage you to contact us. Many issues can be resolved quickly and amicably.",
    
    // Terms Summary content
    termsSummaryPlay: "Have fun - Rocketanks is for entertainment!",
    termsSummaryBeNice: "Be nice - Respect other players",
    termsSummaryPlayFair: "Play fair - No cheats or hacks",
    termsSummaryFinal: "Purchases are final - Think before you buy",
    termsSummaryContact: "Contact us - We're here to help",
    
    // Privacy Policy page
    privacyTitle: "Privacy Policy",
    privacySubtitle: "How we protect your data",
    privacyHeroTitle: "Your privacy is important",
    privacyHeroDesc: "We clearly explain how we handle your data in Rocketanks.",
    
    // Privacy intro
    privacyIntro: "Introduction",
    privacyIntroDesc: "NAgentes Tecnologia, developer of Rocketanks, is committed to protecting your privacy. This policy explains what information we collect, how we use it, and your rights regarding your personal data.",
    
    // Privacy sections titles
    privacyCollection: "Data We Collect",
    privacyUsage: "How We Use Your Data",
    privacySecurity: "Data Security",
    privacyRights: "Your Rights (LGPD)",
    privacyCookies: "Cookies and Local Storage",
    privacyMinors: "Protection of Minors",
    privacyChanges: "Changes to this Policy",
    
    // Privacy Collection content
    privacyAccountData: "Account Data",
    privacyAccountEmail: "Email (for login and communication)",
    privacyAccountUsername: "Username (public alias)",
    privacyAccountBirth: "Date of birth (for age verification)",
    privacyAccountAvatar: "Selected avatar",
    privacyGameData: "Game Data",
    privacyGameProgress: "Game progress (tanks, levels, currencies)",
    privacyGameStats: "Match statistics (wins, losses, score)",
    privacyGameSettings: "Configuration preferences",
    privacyGamePurchases: "In-app purchase history",
    privacyTechData: "Technical Data",
    privacyTechDevice: "Device type and operating system",
    privacyTechIP: "IP address (for security)",
    privacyTechLogs: "Error and diagnostic logs",
    
    // Privacy Usage content
    privacyUsageImprove: "Provide and improve the gaming experience",
    privacyUsageSave: "Save your progress in the cloud",
    privacyUsagePayments: "Process payments and purchases",
    privacyUsageNotify: "Send important notifications about your account",
    privacyUsageFraud: "Prevent fraud and misuse",
    privacyUsageMatchmaking: "Improve matchmaking and balancing",
    privacyNeverSell: "We never sell your data to third parties",
    privacyNeverShare: "We never share your email with advertisers",
    
    // Privacy Security content
    privacySecurityDesc: "We implement enterprise-level security measures to protect your data:",
    privacySecurityTLS: "TLS/SSL Encryption",
    privacySecurityTLSDesc: "All data in transit is encrypted",
    privacySecurityDB: "Secure Database",
    privacySecurityDBDesc: "Data stored with encryption at rest",
    privacySecurityTokens: "Secure Tokens",
    privacySecurityTokensDesc: "Authentication via OAuth2 and JWT",
    privacySecurityMonitor: "Monitoring",
    privacySecurityMonitorDesc: "24/7 suspicious access detection",
    
    // LGPD Rights (Brazil)
    lgpdRights: "Under the General Data Protection Law (LGPD), you have the following rights:",
    lgpdAccess: "Access",
    lgpdAccessDesc: "Know what data we have about you",
    lgpdCorrection: "Correction",
    lgpdCorrectionDesc: "Correct incorrect or incomplete data",
    lgpdDeletion: "Deletion",
    lgpdDeletionDesc: "Request the deletion of your data",
    lgpdPortability: "Portability",
    lgpdPortabilityDesc: "Receive your data in a readable format",
    lgpdRevocation: "Revocation",
    lgpdRevocationDesc: "Withdraw consent at any time",
    lgpdExercise: "To exercise your rights:",
    
    // Privacy Cookies content
    privacyCookiesDesc: "We use local storage (localStorage) to:",
    privacyCookiesLogin: "Keep you logged in between sessions",
    privacyCookiesSettings: "Save configuration preferences",
    privacyCookiesCache: "Store game resource cache",
    privacyCookiesNoTracking: "We do not use third-party tracking cookies for targeted advertising.",
    
    // Privacy Minors content
    privacyMinorsAge: "Rocketanks is rated for ages 10 and up.",
    privacyMinorsImportant: "Important:",
    privacyMinorsConsent: "Players under 18 need parental consent",
    privacyMinorsNoCollect: "We do not intentionally collect data from users under 13",
    privacyMinorsPurchases: "In-app purchases require guardian authorization",
    
    // Privacy Changes content
    privacyChangesDesc: "We may update this policy periodically. When we make significant changes:",
    privacyChangesNotify: "We will notify you by email or in the app",
    privacyChangesDate: "The \"last updated\" date will be modified",
    privacyChangesEffective: "Changes take effect 30 days after publication",
  },

  infinite: {
    bestWave: "Best",
    newRecord: "NEW RECORD!",
    noRecordYet: "No record yet",
    medals: "Medals",
    nextMedal: "Next medal: wave {wave}",
    statusBurning: "On fire",
    statusSoaked: "Soaked",
    statusMuddy: "Muddy",
    statusHint: "Water puts out fire",
    contract: "Wave challenge",
    contractComplete: "Challenge complete!",
    contractFailed: "Challenge lost",
    contractReward: "Bonus",
    chooseUpgrade: "Pick an upgrade",
    chooseUpgradeHint: "It lasts the whole run",
    reroll: "Swap cards",
    rerollsLeft: "{count} left",
    skipDraft: "Skip",
    yourBuild: "Your tank",
    waveLabel: "Wave {wave}",
    bossWaveIn: "BOSS in {count}",
    bossWaveNow: "BOSS",
    eliteWave: "ELITE",
    hazardWarning: "Watch out",
    tapToSkip: "Tap to skip",
    runSummary: "Run summary",
    contractsCleared: "Challenges cleared",

    cards: {
      twin_barrel: { title: "Twin Barrel", description: "+1 shot per volley, each a bit weaker" },
      heavy_shell: { title: "Heavy Shell", description: "+18% damage" },
      piercing_round: { title: "Piercing Round", description: "Your shot punches through one more enemy" },
      hot_powder: { title: "Hot Powder", description: "25% faster shots — easier to land" },
      siege_cannon: { title: "Siege Cannon", description: "+55% damage, but the shell travels slower" },
      reinforced_hull: { title: "Reinforced Hull", description: "+25 max health" },
      composite_armor: { title: "Composite Armor", description: "Absorbs 18% of incoming damage" },
      heavy_plating: { title: "Heavy Plating", description: "Absorbs 32% of damage and +40 health, but slows you down" },
      field_repair: { title: "Field Repair", description: "+18 healing at the end of each wave" },
      race_treads: { title: "Racing Treads", description: "+12% speed" },
      amphibious_kit: { title: "Amphibious Kit", description: "Water and mud barely slow you" },
      studded_treads: { title: "Studded Treads", description: "No more sliding on ice" },
      heat_shield: { title: "Heat Shield", description: "Lava hurts far less" },
      all_terrain: { title: "All-Terrain", description: "Water, ice and lava barely bother you" },
      item_magnet: { title: "Item Magnet", description: "Nearby items come to you" },
      strong_magnet: { title: "Strong Magnet", description: "Pulls items from much farther" },
      scavenger: { title: "Scavenger", description: "+4 health per enemy destroyed" },
      second_thought: { title: "Second Thought", description: "+1 card swap per run" },
      glass_cannon: { title: "Glass Cannon", description: "+60% damage, but −35 max health" },
      reckless_charge: { title: "Reckless Charge", description: "+30% speed, but −20 max health" },
      overload: { title: "Overload", description: "+35% damage and +2 shots, but −25 health" },
      vampire_core: { title: "Vampire Core", description: "+10 health per kill, slightly less damage" },
      bulwark: { title: "Bulwark", description: "+90 health and extra healing, but slower" },
      storm_of_lead: { title: "Storm of Lead", description: "+3 shots per volley, each weaker" },
    },

    contracts: {
      no_damage: { title: "Untouched", description: "Finish the wave without taking damage" },
      no_items: { title: "Aim Only", description: "Finish the wave without using items" },
      speedrun: { title: "Lightning", description: "Clear the wave in under {target}s" },
      combo: { title: "Streak", description: "{target} kills within 6 seconds" },
      priority_target: { title: "Marked Target", description: "Destroy the {target} before anything else" },
      demolisher: { title: "Demolisher", description: "Destroy {target} blocks" },
      long_range: { title: "Long Shot", description: "{target} kills from far away" },
      close_quarters: { title: "Point Blank", description: "{target} kills up close" },
      full_health: { title: "Unscathed", description: "Finish the wave above {target}% health" },
      firewalker: { title: "Firewalker", description: "{target} kills while on fire" },
    },

    enemyKinds: {
      normal: "Soldier",
      heavy: "Armored",
      fast: "Runner",
      sniper: "Sniper",
      boss: "Commander",
    },

    archetypes: {
      open: "Open Field",
      lake: "Broken Lake",
      marsh: "Marshland",
      glacier: "Cracked Glacier",
      archipelago: "Archipelago",
      forge: "The Forge",
      crater: "Caldera",
    },

    worldModifiers: {
      none: { title: "", description: "" },
      swarm: { title: "Swarm", description: "Many enemies, all fragile" },
      juggernaut: { title: "Juggernauts", description: "Few enemies, very tough" },
      sharpshooter: { title: "Sharpshooters", description: "Snipers everywhere" },
      blitz: { title: "Blitz", description: "Everyone moves faster" },
      volatile: { title: "Volatile", description: "Enemies explode when they die" },
    },

    hazards: {
      water: "Water — slows you down",
      lava: "Lava — it burns!",
      ice: "Ice — you slide",
      mud: "Mud — you bog down",
    },
  },
  sofa: {
    menuTitle: "Couch Mode",
    menuDesc: "Up to 4 players on one screen, on a phone, PC or TV. Controllers or keyboard; CPUs fill empty slots.",
    title: "Couch Mode",
    subtitle: "Up to 4 players on one screen: phone, PC or TV",
    player: "Player {n}",
    keyboardJoin: "or {key} on the keyboard",
    joined: "Ready",
    leaveHint: "B to leave",
    modeCoop: "Co-op",
    modeCoopDesc: "Together against waves of bots. 3 lives each; whoever falls returns when the wave ends.",
    modeVersus: "Duel",
    modeVersusDesc: "Free-for-all. First to {kills} kills wins.",
    arena: "Arena",
    back: "Back",
    tvTitle: "Play on a TV (optional)",
    tvStep1: "Pair the controllers with the device running the game (up to 4). On a PC, the keyboard works too.",
    tvStep2: "Get the picture on the TV: AirPlay on iPhone, Smart View on Android or an HDMI cable on PC.",
    tvStep3: "Enable the TV's Game Mode for less lag. On a phone, play in landscape.",
    noGamepadApi: "This browser can't read controllers. Use the keyboard or another browser.",
    keyboardWasd: "Keyboard: WASD + Space",
    keyboardArrows: "Keyboard: Arrows + Enter",
    wave: "Wave {n}",
    firstTo: "First to {n}",
    getReady: "Get ready",
    go: "Fight!",
    waveCleared: "Wave {n} cleared!",
    nextWaveIn: "Next wave in {n}",
    respawnIn: "Back in {n}",
    paused: "Paused",
    quit: "Quit",
    pauseHint: "D-pad chooses · A confirms · B or Start resumes",
    disconnected: "Player {n}'s controller disconnected",
    reconnectHint: "Turn the controller on and press A",
    victory: "{name} wins!",
    reachedWave: "You reached wave {n}",
    rematch: "Rematch",
    overHint: "D-pad chooses · A confirms · B back to lobby",
    lives: "Lives",
    modeRivals: "Rivals",
    modeRivalsDesc: "Same waves, but every kill scores for whoever fired. After {waves} waves, the higher score wins.",
    difficulty: "Difficulty",
    diffEasy: "Easy",
    diffNormal: "Normal",
    diffHard: "Hard",
    killsLabel: "Kills to win",
    noSpecial: "No power",
    score: "{n} pts",
    waitingRescue: "Out! Your partner can rescue you",
    rescuing: "Rescuing… {n}%",
    annKilled: "{a} took out {b}!",
    annDown: "{name} is down!",
    annEliminated: "{name} is out — go rescue!",
    annRescued: "{a} rescued {b}!",
    annBoss: "Boss incoming!",
    annBossDown: "Boss defeated!",
    annPickup: "{name}: {item}",
    hintPad: "Left stick moves · Right stick aims · RT/A fires (hold to charge) · LB/X uses item",
    hintWasd: "WASD moves · Space fires (hold to charge) · E/Q uses item",
    hintArrows: "Arrows move · Enter fires (hold to charge) · Right Shift uses item",
    accuracy: "Accuracy",
    damage: "Damage",
    reward: "+{n} chips",
    newRecord: "New record!",
    draw: "Draw!",
    stepJoin: "Players",
    stepTanks: "Tanks",
    stepSetup: "Match",
    stepOf: "Step {n} of {total}",
    joinTitle: "Who's playing?",
    joinText: "Pick how many tanks. Whoever presses A on a controller (or Space/Enter on the keyboard) plays; the remaining slots go to the CPU.",
    continue: "Continue",
    continueHint: "A or Start to pick tanks",
    confirmTank: "Confirm ({key})",
    tanksTitle: "Choose your tank",
    tanksText: "Move your token to a tank and confirm. CPUs pick theirs at random.",
    tanksPadHint: "D-pad picks · LB/RB power · A confirms · B back",
    tanksKeysHint: "Keyboard: WASD, Q/E and Space · arrows, [ ] and Enter",
    killsShort: "{n} kills",
    setupTitle: "Set up the match",
    setupText: "Mode, difficulty and arena. Any player can choose.",
    modeLabel: "Mode",
    startMatch: "Start match",
    setupHint: "▲ ▼ select · ◀ ▶ change · A confirm · Start begins · B back",
    leave: "Leave",
    prevPower: "Previous power",
    nextPower: "Next power",
    pairTitle: "How to pair a controller",
    pairWhereIos: "On iPhone, open Settings → Bluetooth and tap the controller's name under “Other Devices”.",
    pairWhereAndroid: "On Android, open Settings → Bluetooth (or Connected devices) → Pair new device and tap the controller's name.",
    pairWhereDesktop: "On a computer, open the Bluetooth settings, add a device and pick the controller.",
    pairBrandOther: "Others",
    pairXbox: "Turn it on with the Xbox button and hold the pair button (top, next to the USB port) until the Xbox logo blinks fast.",
    pairPlaystation: "Hold Create and PS together (Share and PS on PS4) until the light blinks fast.",
    pairNintendo: "Pro Controller: hold the sync button on top, next to the USB port. Joy-Con: hold the sync button on the side rail until the lights sweep.",
    pairOther: "Check the manual for pairing mode. 8BitDo: turn it on in X (Xbox) or S (Switch) mode and hold the pair button.",
    pairThen: "Back in the game, press A on the controller to join.",
    pairNote: "Pairing is done by the system, once per controller: no game can pair on its own. After that, just turn the controller on.",
    diagTitle: "Diagnostics",
    diagHint: "Press buttons on each controller: all of them should show up here. On iPhone, a controller only appears after its first button press.",
    diagApiOk: "This browser can read controllers",
    diagNoPads: "No controller seen yet. Press a button on each one.",
    diagStandard: "Standard mapping",
    diagNonStandard: "Non-standard mapping",
    diagRumble: "Rumble",
    diagButtons: "Buttons",
    diagShowFps: "Show FPS in the match",
    cpuName: "CPU {n}",
    map: "Map",
    slotHuman: "Player",
    slotOff: "Empty",
    slotTakeHint: "Press A to join",
    slotClickHint: "Click to play on the keyboard",
    keyboardPlay: "Play with the keyboard",
    keyboardPlayHint: "Click here or press Space · on a controller, press A",
    slotAdd: "Add slot",
    playersLabel: "Players",
    cpuLevel: "CPU level",
    needHuman: "Press A on a controller to join",
    summaryPlayers: "{n} tanks",
    summaryCpu: "{n} CPU",
    playersPadHint: "A joins · B leaves · X/Y more or fewer slots · Start continues",
    playersKeysHint: "Keyboard (up to 2 on one): Space (P1), Enter (P2); 2, 3 and 4 set how many",
    shuffle: "Shuffle",
    readyToFight: "Ready to battle!",
    readyToFightHint: "A or Start to continue",
  },
};

export default en;
